import { useCallback, useState } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import { MAP_LAYOUT_VERSION } from '@interfaces/map';

import { useEditorMode } from './EditorModeContext';
import { useMapEditor } from './MapEditorContext';
import { useActiveBrandingId } from './useActiveBrandingId';
import { useMapEditorCatalog } from './useMapEditorCatalog';
import { buildSpawnLayoutPayload, layoutJsonBudget } from './mapEditorUnreal';
import { toPersistedFinishes, toPersistedRooms } from './mapEditorRooms';

export interface MapEditorPublish {
  publish: () => void;
  // Size of the publish this hub refused, in bytes; zero once one goes through. The author has to act
  // on it — shorten the layout, or move per-room finishes to the layout default — so it is state to
  // show rather than an error to log.
  oversizeBytes: number;
}

/**
 * The one thing in the map editor that reaches the other participants.
 *
 * Extracted from the Layout panel when the layout-wide controls moved out of it and onto their own
 * plaque. Nothing about the publish changed in the move, and it is a hook rather than two copies for
 * exactly that reason: the budget refusal, the order of the two writes and the autosave reporting are
 * one decision each, and the version that lived in a panel is the version that has been in production.
 *
 * Unreal is told first and the branding write follows. The spawn is what the author is watching for,
 * and holding it behind a round-trip would make Publish feel broken on a slow connection; a failed
 * write costs the layout its persistence, not its publish.
 *
 * @returns The publish action and the size of the last publish that would not fit.
 */
export const useLayoutPublish = (): MapEditorPublish => {
  const { rooms, finishes, startElement, markPublished } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const currentBrandingId = useActiveBrandingId();
  const { setAutosaveStatus } = useEditorMode();
  const { elements: catalogElements, defaultEndElement } =
    useMapEditorCatalog();

  // Held rather than derived on every render because the measurement is of a message that was
  // actually built — asking "would this fit" continuously would serialise the whole layout on every
  // keystroke elsewhere.
  const [oversizeBytes, setOversizeBytes] = useState(0);

  const publish = useCallback(() => {
    // Built before it is sent rather than through spawnLayoutInUnreal, because the message has to be
    // measured and the only honest measurement is of the message itself.
    //
    // The catalog rides along so the authority does not have to resolve the ids itself — see
    // describeRoom in mapEditorUnreal. The cap id goes with it for the same reason: a packaged
    // server cannot read the catalog to resolve its own defaultEndElement.
    const payload = buildSpawnLayoutPayload(
      rooms,
      /*preview=*/ false,
      catalogElements,
      /*draftActive=*/ false,
      defaultEndElement,
      startElement,
      finishes,
    );

    // Refused here rather than left to the authority. Over its own budget the authority declines the
    // spawn and logs what to shorten — into its process, where the author is not looking — so the
    // publish would appear to succeed and change nothing. Stopping first is what lets the UI say it,
    // and the number it stops at is under the authority's on purpose (see LAYOUT_JSON_BUDGET_BYTES).
    const budget = layoutJsonBudget(payload);
    if (budget.overBy > 0) {
      setOversizeBytes(budget.bytes);
      return;
    }
    setOversizeBytes(0);

    markPublished(rooms, finishes, startElement);
    sendMessageToUnreal(payload);

    if (!currentBrandingId) {
      return;
    }

    // Saved as the author's intent, not as what Unreal ended up building — those differ whenever a
    // room is refused for overlapping, and persisting the built list would silently rewrite the
    // layout the author authored.
    //
    // Each room goes out with the key it already has. Nothing is minted here: a publish that re-keyed
    // the layout would re-point every piece of content assigned to it.
    setAutosaveStatus('saving');
    BrandingsService.updateBranding(currentBrandingId, {
      layout: {
        version: MAP_LAYOUT_VERSION,
        rooms: toPersistedRooms(rooms),
        // Omitted rather than written empty when no lobby is chosen, so a layout nobody has switched
        // the entrance on is byte-identical to what this hub wrote before the field existed — and,
        // more to the point, so that reading it back cannot turn "opens on the map's own entrance"
        // into a lobby id somebody has to resolve.
        ...(startElement ? { startElement } : {}),
        // Written alongside the rooms rather than under them: an override is addressed to a placement
        // key, and keeping it out of the entries is what makes reading it independent of how the room
        // list itself parsed. See readPersistedFinishes.
        ...toPersistedFinishes(finishes),
      },
    })
      .then(() => setAutosaveStatus('saved'))
      .catch((saveError: Error) => {
        console.error(
          `Failed to save the map layout: ${saveError?.message}`,
          saveError,
        );
        setAutosaveStatus('error');
      });
  }, [
    sendMessageToUnreal,
    rooms,
    finishes,
    markPublished,
    currentBrandingId,
    setAutosaveStatus,
    catalogElements,
    defaultEndElement,
    startElement,
  ]);

  return { publish, oversizeBytes };
};
